import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { RepairRequest, UserStatus } from '../types/firestore';
import { canSubmitRepairRequest, getUserStatus } from '../utils/accessControl';
import {
  getRepairRequestId,
  getRepairRequestUnreadState,
  REPAIR_REQUEST_CLIENT_UNREAD_LEGACY_TYPES,
  REPAIR_REQUEST_UNREAD_FIELDS,
  REPAIR_REQUEST_UNREAD_SECTIONS,
} from '../utils/repairRequestReadModel';

export function useUnreadClientActivities() {
  const { currentUser, userProfile, claims } = useAuth();
  const [sectionRequests, setSectionRequests] = useState<RepairRequest[]>([]);
  const [legacyRequests, setLegacyRequests] = useState<RepairRequest[]>([]);
  const [loadedSections, setLoadedSections] = useState(false);
  const [loadedLegacy, setLoadedLegacy] = useState(false);

  const isActiveClient =
    canSubmitRepairRequest(userProfile, claims) &&
    getUserStatus(userProfile, claims) === UserStatus.ACTIVE;

  const unreadRequests = useMemo(() => {
    const byId = new Map<string, RepairRequest>();
    sectionRequests.forEach((req) => {
      const key = getRepairRequestId(req);
      if (key) {
        byId.set(key, req);
      }
    });
    legacyRequests.forEach((req) => {
      const key = getRepairRequestId(req);
      if (key && !byId.has(key)) {
        byId.set(key, req);
      }
    });
    const data = Array.from(byId.values()).filter(
      (req) => getRepairRequestUnreadState(req, 'client').sections.length > 0
    );
    data.sort((a, b) => {
      const timeA = a.lastClientActivityAt?.toDate?.().getTime?.() || 0;
      const timeB = b.lastClientActivityAt?.toDate?.().getTime?.() || 0;
      return timeB - timeA;
    });
    return data;
  }, [sectionRequests, legacyRequests]);

  const loading = !loadedSections || !loadedLegacy;

  useEffect(() => {
    if (!currentUser || !isActiveClient) {
      setSectionRequests([]);
      setLegacyRequests([]);
      setLoadedSections(true);
      setLoadedLegacy(true);
      return;
    }

    setSectionRequests([]);
    setLegacyRequests([]);
    setLoadedSections(false);
    setLoadedLegacy(false);

    const baseCollection = collection(firestore, 'repairRequests');
    const unreadFields = REPAIR_REQUEST_UNREAD_FIELDS.client;

    const sectionsQuery = query(
      baseCollection,
      where(unreadFields.ownerField, '==', currentUser.uid),
      where(unreadFields.sectionsField, 'array-contains-any', REPAIR_REQUEST_UNREAD_SECTIONS)
    );

    const legacyQuery = query(
      baseCollection,
      where(unreadFields.ownerField, '==', currentUser.uid),
      where(unreadFields.legacyTypeField, 'in', REPAIR_REQUEST_CLIENT_UNREAD_LEGACY_TYPES)
    );

    const unsubscribeSections = onSnapshot(
      sectionsQuery,
      (snapshot) => {
        const data = snapshot.docs.map(
          (doc) => ({ uid: doc.id, id: doc.id, ...doc.data() }) as RepairRequest
        );
        setSectionRequests(data);
        setLoadedSections(true);
      },
      (err) => {
        console.error('Client unread sections snapshot error', err);
        setLoadedSections(true);
      }
    );

    const unsubscribeLegacy = onSnapshot(
      legacyQuery,
      (snapshot) => {
        const data = snapshot.docs.map(
          (doc) => ({ uid: doc.id, id: doc.id, ...doc.data() }) as RepairRequest
        );
        setLegacyRequests(data);
        setLoadedLegacy(true);
      },
      (err) => {
        console.error('Client unread legacy snapshot error', err);
        setLoadedLegacy(true);
      }
    );

    return () => {
      unsubscribeSections();
      unsubscribeLegacy();
    };
  }, [currentUser, isActiveClient]);

  return { unreadRequests, loading };
}
